import React from 'react';
import Icon from './Icon';
import './CrisisResources.css';

/**
 * Shown inline when the safety layer detects a message that may indicate
 * crisis (self-harm, abuse, a medical emergency, or a direct threat to
 * someone else), under that
 * turn's answer. Written to be gentle
 * even when the detection is a false positive — it offers help without
 * accusing or interrupting.
 *
 * The only inverted block on any guest screen (BRAND_SPEC §11.1): white on
 * Night, black on Light reading. The words, every number and their order are
 * unchanged; each line is now a 56 px button with an icon. It never uses the
 * danger colour: a disclosure is not an error.
 */
function CrisisLine({ href, icon, action, service, aside, primary = false, external = false }) {
  return (
    <a
      className={`crisis-btn${primary ? ' call' : ''}`}
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      <Icon name={icon} size={20} />
      <span className="crisis-btn-text">
        <span className="crisis-btn-action">{action}</span>
        {service && <span className="crisis-btn-service">{service}</span>}
      </span>
      {aside && <span className="crisis-btn-aside">{aside}</span>}
    </a>
  );
}

/**
 * A direct threat to someone else ("I'm going to hurt him"): the same card,
 * with 911 first and words for the person who might do the harm. The 988
 * Lifeline also answers people afraid of hurting someone.
 */
function ThreatResources() {
  return (
    <div className="crisis-card" role="note" aria-label="Support resources">
      <div className="crisis-card-heading" role="heading" aria-level={2}>Before anyone gets hurt, real help is close</div>
      <p className="crisis-card-lead">
        If you're thinking about hurting someone, or someone could get hurt, please reach out right now.
        These are real people, available anytime, free and confidential:
      </p>
      <ul className="crisis-card-list">
        <li>
          <CrisisLine href="tel:911" icon="octagon-alert" action="Someone in danger? Call 911" primary />
        </li>
        <li>
          <CrisisLine href="tel:988" icon="phone" action="Call or text 988" service="Talk it through, before anything happens" aside="24/7" />
        </li>
        <li>
          <CrisisLine href="sms:741741" icon="message-square" action="Text HOME to 741741" service="Crisis Text Line" />
        </li>
      </ul>
      <p className="crisis-card-person">
        <strong>Talk to a real person at The Net</strong> —{' '}
        <a href="https://www.thenet.org/contact/" target="_blank" rel="noopener noreferrer">
          reach out to the church
        </a>{' '}
        or come Sunday, 9:00 or 10:45 AM at Campus 805, Huntsville
      </p>
    </div>
  );
}

/**
 * A medical emergency first (round 2 review, H3): too much of something
 * taken, or someone who can't be woken or isn't breathing. 911 is the main
 * button, Poison Control says what to do while help is on the way, and 988
 * comes after, for if any of it was on purpose (functions/guards.js
 * medicalEmergency; the crisis response's own overdose example).
 */
function MedicalResources() {
  return (
    <div className="crisis-card" role="note" aria-label="Emergency resources">
      <div className="crisis-card-heading" role="heading" aria-level={2}>This may be a medical emergency</div>
      <p className="crisis-card-lead">
        If someone took too much of something, can't be woken, or isn't breathing normally, call 911 right now.
        Stay with them; the dispatcher will tell you what to do.
      </p>
      <ul className="crisis-card-list">
        <li>
          <CrisisLine href="tel:911" icon="octagon-alert" action="Call 911 now" service="Emergency help" primary />
        </li>
        <li>
          <CrisisLine href="tel:18002221222" icon="phone" action="Call 1-800-222-1222" service="Poison Control: what to do while help is on the way" aside="24/7" />
        </li>
        <li>
          <CrisisLine href="tel:988" icon="phone" action="Call or text 988" service="If any part of this was on purpose" aside="24/7" />
        </li>
      </ul>
      <p className="crisis-card-person">
        <strong>Talk to a real person at The Net</strong> —{' '}
        <a href="https://www.thenet.org/contact/" target="_blank" rel="noopener noreferrer">
          reach out to the church
        </a>{' '}
        or come Sunday, 9:00 or 10:45 AM at Campus 805, Huntsville
      </p>
    </div>
  );
}

function CrisisResources({ category }) {
  if (category === 'harm_others') return <ThreatResources />;
  if (category === 'medical') return <MedicalResources />;
  return (
    <div className="crisis-card" role="note" aria-label="Support resources">
      <div className="crisis-card-heading" role="heading" aria-level={2}>You matter, and real help is close</div>
      <p className="crisis-card-lead">
        If anything in your life feels heavy right now, you don't have to carry it alone.
        These are real people, available anytime, free and confidential:
      </p>
      <ul className="crisis-card-list">
        <li>
          <CrisisLine href="tel:988" icon="phone" action="Call or text 988" service="988 Suicide & Crisis Lifeline" aside="24/7" primary />
        </li>
        <li>
          <CrisisLine href="sms:741741" icon="message-square" action="Text HOME to 741741" service="Crisis Text Line" />
        </li>
        {category === 'abuse' && (
          <>
            <li>
              <CrisisLine href="tel:18004224453" icon="phone" action="Call 1-800-422-4453" service="Childhelp National Child Abuse Hotline" />
            </li>
            <li>
              <CrisisLine href="tel:18007997233" icon="phone" action="Call 1-800-799-7233" service="National Domestic Violence Hotline" />
            </li>
          </>
        )}
        <li>
          <CrisisLine href="tel:911" icon="octagon-alert" action="In immediate danger? Call 911" />
        </li>
      </ul>
      <p className="crisis-card-person">
        <strong>Talk to a real person at The Net</strong> —{' '}
        <a href="https://www.thenet.org/contact/" target="_blank" rel="noopener noreferrer">
          reach out to the church
        </a>{' '}
        or come Sunday, 9:00 or 10:45 AM at Campus 805, Huntsville
      </p>
      <p className="crisis-card-more">
        If money, health, housing or food is part of what's heavy,{' '}
        <a href="https://www.findhelp.org" target="_blank" rel="noopener noreferrer">findhelp.org</a>{' '}
        finds free and reduced-cost help near you — just enter your ZIP code.
      </p>
    </div>
  );
}

export default CrisisResources;
